<script lang="ts">
  import { Object3D, Vector3, WebGLRenderer } from 'three'
  import { useProjectQuery } from '../../queries'
  import { storeScene } from '../../stores/scene'
  import queryClient from '../../constants/query-client'
  import { onDestroy, onMount, setContext } from 'svelte'
  import { usePageTitle } from '../../hooks/page-title'
  import i18n from '../../i18n'
  import LeftBar from './components/LeftBar/LeftBar.svelte'
  import { Canvas } from '@threlte/core'
  import RightBar from './components/RightBar/RightBar.svelte'
  import SceneControlsIsland from '../../components/scene-controls-island/SceneControlsIsland.svelte'
  import Scene from '../../components/scene/Scene.svelte'
  import { isPerspectiveCamera } from '../../stores/camera'
  import SceneAutoSave from '../../components/scene-auto-save/SceneAutoSave.svelte'
  import LoadingState from './components/LoadingState.svelte'
  import { queryReactiveAccessor } from '../../helpers/tanstack-query'
  import useSceneQueries from './hooks/useSceneQueries'
  import { writable } from 'svelte/store'
  import useCanvasSelection from '../../hooks/canvas-selection'

  const { route } = $props()

  // Derive projectId directly from route params - reactive to URL changes
  const projectId = $derived(route.result.path.params.id)

  // Create a writable store that updates when projectId changes
  /* svelte-ignore state_referenced_locally */
  const projectIdStore = writable<string | undefined>(projectId)
  setContext('projectId', projectIdStore)

  // Track which project has been loaded to prevent duplicate loads
  let loadedProjectId = $state<string | undefined>(undefined)

  const { loadProjectSceneMutation } = useSceneQueries()
  const { clear: clearSelection } = useCanvasSelection()

  // Update projectIdStore when projectId changes from route
  $effect(() => {
    projectIdStore.set(projectId)
  })

  // Single effect to handle scene loading when projectId changes
  $effect(() => {
    const scene = $storeScene

    // Only load if projectId is defined, scene exists, and we haven't loaded this project yet
    if (
      projectId
      && scene
      && loadedProjectId !== projectId
      && !loadProjectSceneMutation.isPending
    ) {
      loadProjectSceneMutation
        .mutateAsync(projectId)
        .then(() => {
          loadedProjectId = projectId
        })
        .catch((error) => {
          console.error('Failed to load project scene:', error)
          // Reset loadedProjectId on error to allow retry
          loadedProjectId = undefined
        })
    }
  })

  /* svelte-ignore state_referenced_locally */
  const getProjectQuery = useProjectQuery(projectId)
  const project = $derived.by(
    queryReactiveAccessor(getProjectQuery, item => item?.data),
  )

  // Update page title when project data changes
  $effect(() => {
    const projectName = getProjectQuery.data?.data.name
    if (projectName) {
      usePageTitle(
        $i18n.t('project.metaTitle', {
          projectName,
        }),
      )
    }
  })

  onMount(() => {
    Object3D.DEFAULT_UP = new Vector3(0, 0, 1)
    $isPerspectiveCamera = true
  })

  onDestroy(() => {
    clearSelection()

    if ($storeScene) {
      $storeScene.clear()
      $storeScene = null
    }

    queryClient.invalidateQueries({ queryKey: ['canvas'] })
  })

  const projectContextState = $derived<ProjectContext>({
    project,
  })
  setContext('projectContext', {
    get project() {
      return projectContextState.project
    },
  })
</script>

{#if project}
  {#key projectId}
    <div class="container">
      <SceneAutoSave
        loading={!loadProjectSceneMutation.isSuccess
          || loadedProjectId !== projectId}
      />
      <div class="grid">
        <LeftBar sceneLoaded={loadProjectSceneMutation.isSuccess && loadedProjectId === projectId} />
        <div class="canvas">
          <Canvas
            createRenderer={(canvas) => {
              return new WebGLRenderer({
                canvas,
                preserveDrawingBuffer: true,
                stencil: true,
              })
            }}
            renderMode="always"
          >
            <Scene />
          </Canvas>
          <LoadingState loading={loadProjectSceneMutation.isPending} />
          <SceneControlsIsland />
        </div>
        <RightBar />
      </div>
    </div>
  {/key}
{/if}

<style lang="scss">
  .container {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    width: 100vw;

    .grid {
      align-items: stretch;
      display: flex;
      flex-direction: row;
      flex: 1 1 auto;
      overflow: hidden;
      justify-content: space-between;
      width: 100vw;

      .canvas {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        position: relative;
      }
    }
  }
</style>
